import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Globe, ChevronRight } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_interpret-now/artifacts/8sijz41w_PassportTalk.png";

const languages = [
  {
    code: "en",
    flagUrl: "https://flagcdn.com/w40/us.png",
    flagUrl2x: "https://flagcdn.com/w80/us.png",
    labelKey: "englishCardTitle",
    descKey: "englishCardDesc",
    nativeLabel: "English",
    color: "#0EA5E9",
    fullExperience: true
  },
  {
    code: "es",
    flagUrl: "https://flagcdn.com/w40/es.png",
    flagUrl2x: "https://flagcdn.com/w80/es.png",
    labelKey: "spanishCardLabel",
    nativeLabel: "Español",
    color: "#f59e0b",
    fullExperience: false
  },
  {
    code: "fr",
    flagUrl: "https://flagcdn.com/w40/fr.png",
    flagUrl2x: "https://flagcdn.com/w80/fr.png",
    labelKey: "frenchCardLabel",
    nativeLabel: "Français",
    color: "#3b82f6",
    fullExperience: false
  },
  {
    code: "ar",
    flagUrl: "https://flagcdn.com/w40/sa.png",
    flagUrl2x: "https://flagcdn.com/w80/sa.png",
    labelKey: "arabicCardLabel",
    nativeLabel: "العربية",
    color: "#10b981",
    fullExperience: false,
    rtl: true
  }
];

// Asia region card
const asiaCard = {
  type: "region",
  code: "asia",
  icon: "🌏",
  nativeLabel: "Asia",
  subtitle: "Chinese • Japanese • Korean • Vietnamese • Thai",
  color: "#dc2626"
};

const LanguageSelectionPage = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [selected, setSelected] = useState(null);

  const handleSelection = (lang) => {
    setSelected(lang.code);
    
    // Change i18n language (this also updates localStorage and RTL)
    i18n.changeLanguage(lang.code);
    
    // Save primary language
    localStorage.setItem('passportTalk_primaryLanguage', lang.code);
    
    // Route based on selection
    setTimeout(() => {
      if (lang.fullExperience) {
        // English speakers get full experience
        navigate('/home');
      } else {
        // Non-English speakers go directly to WhatsApp mode
        navigate('/conversation');
      }
    }, 300);
  };

  const handleAsiaSelection = () => {
    setSelected('asia');
    setTimeout(() => {
      navigate('/asia-languages');
    }, 200);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex flex-col items-center justify-center p-6">
      <div className="max-w-md w-full">
        {/* Logo & Header */}
        <div className="text-center mb-8">
          <img 
            src={LOGO_URL} 
            alt="PassportTalk" 
            className="w-16 h-16 mx-auto mb-4 object-contain"
          />
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-outfit mb-2">
            {t('languageSelect.title')}
          </h1>
          <p className="text-slate-600 text-sm">
            {t('languageSelect.subtitle')}
          </p>
        </div>

        {/* Language Options */}
        <div className="space-y-3">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleSelection(lang)}
              className={`w-full p-4 rounded-xl border-2 transition-all duration-200 ease-out text-left group
                ${selected === lang.code 
                  ? `border-[${lang.color}] bg-[${lang.color}]/5 shadow-lg scale-[1.02]` 
                  : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-md hover:-translate-y-0.5'
                }`}
              style={{
                borderColor: selected === lang.code ? lang.color : undefined,
                backgroundColor: selected === lang.code ? `${lang.color}10` : undefined
              }}
              data-testid={`language-${lang.code}`}
              dir={lang.rtl ? 'rtl' : 'ltr'}
            >
              <div className={`flex items-center gap-4 ${lang.rtl ? 'flex-row-reverse' : ''}`}>
                {/* Flag Icon */}
                <div className="w-12 h-8 rounded overflow-hidden shadow-sm border border-slate-200 flex-shrink-0 transition-transform duration-200 ease-out group-hover:scale-[1.04]">
                  <img 
                    src={lang.flagUrl}
                    srcSet={`${lang.flagUrl2x} 2x`}
                    alt={`${lang.nativeLabel} flag`}
                    className="w-full h-full object-cover"
                    loading="eager"
                  />
                </div>
                <div className="flex-1">
                  <h3 className={`text-base font-bold text-slate-900 flex items-center gap-2 ${lang.rtl ? 'flex-row-reverse justify-end' : ''}`}>
                    {lang.nativeLabel}
                    {selected === lang.code && (
                      <ChevronRight className={`w-4 h-4 ${lang.rtl ? 'rotate-180' : ''}`} style={{ color: lang.color }} />
                    )}
                  </h3>
                  {lang.fullExperience && (
                    <p className="text-xs text-slate-500 mt-0.5">
                      {t('languageSelect.englishCardDesc')}
                    </p>
                  )}
                </div>
              </div>
            </button>
          ))}

          {/* Asia Region Card */}
          <button
            onClick={handleAsiaSelection}
            className={`w-full p-4 rounded-xl border-2 transition-all duration-200 ease-out text-left group
              ${selected === 'asia' 
                ? 'shadow-lg scale-[1.02]' 
                : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-md hover:-translate-y-0.5'
              }`}
            style={{
              borderColor: selected === 'asia' ? asiaCard.color : undefined,
              backgroundColor: selected === 'asia' ? `${asiaCard.color}10` : undefined
            }}
            data-testid="language-asia"
          >
            <div className="flex items-center gap-4">
              {/* Region Icon */}
              <div className="w-12 h-8 rounded overflow-hidden shadow-sm border border-slate-200 flex-shrink-0 flex items-center justify-center bg-gradient-to-br from-red-50 to-orange-50 transition-transform duration-200 ease-out group-hover:scale-[1.04]">
                <span className="text-2xl">{asiaCard.icon}</span>
              </div>
              <div className="flex-1">
                <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                  {asiaCard.nativeLabel}
                  {selected === 'asia' && (
                    <ChevronRight className="w-4 h-4" style={{ color: asiaCard.color }} />
                  )}
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  {asiaCard.subtitle}
                </p>
              </div>
            </div>
          </button>
        </div>

        {/* Note */}
        <div className="mt-6 text-center">
          <span className="px-3 py-1 bg-[#0EA5E9]/10 text-[#0EA5E9] text-xs font-semibold rounded-full">
            {t('app.beta')}
          </span>
          <p className="mt-3 text-xs text-slate-400">
            {t('languageSelect.note')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default LanguageSelectionPage;
